import {
  resolveReportCalloutTitle,
  resolveSectionCalloutBody,
  resolveSectionCalloutTitle,
  sectionCalloutHasDisplayContent,
} from '@act/shared/data-transfer-objects';
import type { SectionMetaDto } from '@act/shared/data-transfer-objects';
import type { ReportReadingColumnItem } from './reportPreview.adapter.util';
import { getSectionMeta } from '../utils/reportDisplay.util';
import { ReportCallout } from './ReportCallout';

export interface ReportSectionAuthoringBlocksProps {
  sectionId: string;
  callout: string | null;
  calloutTitle?: string | null;
  calloutEnabled: boolean;
  /** Optional wrapper classes (e.g. tail placement below a hosted section). */
  className?: string;
  /** Wrap in the compact 48px callout band below hosted section bodies. */
  band?: boolean;
  /** Builder preview: edit callout inline instead of side-panel fields. */
  editable?: boolean;
  /** @param value - Updated callout text. */
  onCalloutChange?: (value: string) => void;
  /** @param value - Updated callout title. */
  onCalloutTitleChange?: (value: string) => void;
}

/**
 * Renders an optional author-edited callout below Studio section bodies.
 * @param props - Section metadata flags and row callout field.
 */
export const ReportSectionAuthoringBlocks = ({
  sectionId,
  callout,
  calloutTitle = null,
  calloutEnabled,
  className = '',
  band = false,
  editable = false,
  onCalloutChange,
  onCalloutTitleChange,
}: ReportSectionAuthoringBlocksProps) => {
  const meta = getSectionMeta(sectionId);
  if (!meta) {
    return null;
  }

  const displayBody = resolveSectionCalloutBody(sectionId, callout);
  const displayTitle = resolveReportCalloutTitle(
    resolveSectionCalloutTitle(sectionId, calloutTitle),
  );
  const showCallout =
    meta.supportsCallout &&
    calloutEnabled &&
    (editable || sectionCalloutHasDisplayContent(sectionId, callout));

  if (!showCallout) {
    return null;
  }

  const calloutCard = (
    <article className={`max-w-3xl mx-auto w-full ${className}`.trim()}>
      <ReportCallout
        value={displayBody}
        title={displayTitle}
        editable={editable}
        onChange={onCalloutChange}
        onTitleChange={onCalloutTitleChange}
      />
    </article>
  );

  if (band) {
    return (
      <section className="report-section-callout-band">
        <div className="container">{calloutCard}</div>
      </section>
    );
  }

  return calloutCard;
};

/**
 * @param meta - Section library metadata.
 * @param item - Reading column row with authoring fields.
 * @returns Whether a callout block would render for the row.
 */
export const sectionHasVisibleAuthoring = (
  meta: SectionMetaDto | undefined,
  item: Pick<ReportReadingColumnItem, 'sectionId' | 'callout' | 'calloutEnabled'>,
): boolean => {
  if (!meta) {
    return false;
  }
  return (
    meta.supportsCallout &&
    item.calloutEnabled &&
    sectionCalloutHasDisplayContent(item.sectionId, item.callout)
  );
};
